// C27 — sensitivity analysis: pick a variable, drag a slider, and see total support and each party's net
// spendable income move across the range on a live chart. Non-destructive (the real case is never changed):
// every point is a throwaway recomputation via the engine. Reuses the variation setters.
import { useEffect, useMemo, useState } from 'react';
import type { Case, CaseResult, PartyIndex } from '@divorceos/schema';
import { useCaseView } from '../caseView';
import { compute } from '../model';
import { timeshareWith } from '../../lib/children';
import { withInput, withSpousal, withTimeshare } from '../variation';

type VarKey = 'ts' | 'wA' | 'wB' | 'ss';
interface VarDef { key: VarKey; label: string; min: number; max: number; step: number; current: number; money: boolean; make: (val: number) => Case }

const N = 24;                                   // sample points across the range
const dollars = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

function niceMax(base: number, floor: number): number {
  const raw = Math.max(base * 2, floor);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)) - 1);
  return Math.ceil(raw / mag) * mag || floor;
}

export function Sensitivity() {
  const v = useCaseView();
  const { c, labels: L, result } = v;
  const hasKids = c.children.length > 0;
  const ssPayor: PartyIndex = result?.guideline.ss.payor ?? (c.parties[0].wages >= c.parties[1].wages ? 0 : 1);
  const ssBase = result?.guideline.ss.amount ?? 0;

  // The variables the slider can drive (timeshare only when there are children).
  const vars = useMemo<VarDef[]>(() => {
    const defs: VarDef[] = [];
    if (hasKids) defs.push({ key: 'ts', label: `${L[0]} parenting time`, min: 0, max: 100, step: 1, current: Math.round(timeshareWith(c.children, 0)), money: false, make: (val) => withTimeshare(c, 0, val) });
    const wMax0 = niceMax(c.parties[0].wages, 10000), wMax1 = niceMax(c.parties[1].wages, 10000);
    defs.push({ key: 'wA', label: `${L[0]} wages + salary`, min: 0, max: wMax0, step: Math.max(50, Math.round(wMax0 / 100)), current: c.parties[0].wages, money: true, make: (val) => withInput(c, 0, 'wages', val) });
    defs.push({ key: 'wB', label: `${L[1]} wages + salary`, min: 0, max: wMax1, step: Math.max(50, Math.round(wMax1 / 100)), current: c.parties[1].wages, money: true, make: (val) => withInput(c, 1, 'wages', val) });
    const sMax = niceMax(ssBase, 4000);
    defs.push({ key: 'ss', label: 'Spousal support amount', min: 0, max: sMax, step: Math.max(25, Math.round(sMax / 100)), current: Math.round(ssBase), money: true, make: (val) => withSpousal(c, val, ssPayor) });
    return defs;
  }, [c, L, hasKids, ssBase, ssPayor]);

  const [varKey, setVarKey] = useState<VarKey>(vars[0].key);
  const def = vars.find((d) => d.key === varKey) ?? vars[0];
  const [val, setVal] = useState(def.current);
  // Reset the slider to the variable's current value when the variable (or case) changes.
  useEffect(() => { setVal(def.current); }, [def.key, def.current]);

  const fmtX = (n: number) => (def.money ? dollars(n) : `${Math.round(n)}%`);

  // The curve: total support + each party's net spendable at N points across the range.
  const curve = useMemo(() => {
    const pts: { x: number; total: number; a: number; b: number }[] = [];
    for (let i = 0; i <= N; i++) {
      const x = def.min + ((def.max - def.min) * i) / N;
      try {
        const r = compute(def.make(x));
        pts.push({ x, total: r.guideline.total.amount, a: r.parties[0].guideline.netSpendable, b: r.parties[1].guideline.netSpendable });
      } catch { /* skip a bad point */ }
    }
    return pts;
  }, [def, v.doc.calcAt]);

  // Exact recomputation at the slider position (for the readout + marker).
  const at = useMemo(() => {
    try { const r = compute(def.make(val)); return { total: r.guideline.total.amount, a: r.parties[0].guideline.netSpendable, b: r.parties[1].guideline.netSpendable, payor: r.guideline.total.payor }; }
    catch { return null; }
  }, [def, val]);

  if (!result) return <div className="ph muted" style={{ padding: 20 }}>No result to analyse{v.engineError ? `: ${v.engineError}` : ''}.</div>;

  // Chart geometry.
  const W = 640, H = 300, PL = 58, PR = 16, PT = 14, PB = 34;
  const maxY = Math.max(1, ...curve.flatMap((p) => [p.total, p.a, p.b])) * 1.08;
  const sx = (x: number) => PL + ((x - def.min) / (def.max - def.min || 1)) * (W - PL - PR);
  const sy = (y: number) => PT + (1 - y / maxY) * (H - PT - PB);
  const path = (key: 'total' | 'a' | 'b') => curve.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x).toFixed(1)},${sy(p[key]).toFixed(1)}`).join(' ');
  const yTicks = Array.from({ length: 5 }, (_, i) => (maxY / 4) * i);

  const lines: { key: 'total' | 'a' | 'b'; color: string; label: string }[] = [
    { key: 'total', color: '#8430ce', label: 'Total support' },
    { key: 'a', color: 'var(--pa)', label: `${L[0]} net spendable` },
    { key: 'b', color: 'var(--pb)', label: `${L[1]} net spendable` },
  ];

  return (
    <div className="sens">
      <div className="sens-head">
        <h2>Sensitivity</h2>
        <p>See how total support and each parent’s net spendable income move as one input changes. Nothing here changes your case — it’s a live what-if.</p>
      </div>

      <div className="sens-vars" role="tablist">
        {vars.map((d) => <button key={d.key} type="button" role="tab" className={d.key === varKey ? 'on' : ''} aria-selected={d.key === varKey} onClick={() => setVarKey(d.key)}>{d.label}</button>)}
      </div>

      <div className="sens-chart">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${def.label} sensitivity chart`}>
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PL} y1={sy(t)} x2={W - PR} y2={sy(t)} stroke="var(--line-2)" />
              <text x={PL - 8} y={sy(t) + 3} textAnchor="end" className="sens-axis">{dollars(t)}</text>
            </g>
          ))}
          {/* x marker at the slider value */}
          <line x1={sx(val)} y1={PT} x2={sx(val)} y2={H - PB} stroke="var(--accent)" strokeDasharray="4 3" opacity={0.7} />
          {lines.map((ln) => <path key={ln.key} d={path(ln.key)} fill="none" stroke={ln.color} strokeWidth={ln.key === 'total' ? 2.4 : 1.8} />)}
          {at && lines.map((ln) => <circle key={ln.key} cx={sx(val)} cy={sy(ln.key === 'total' ? at.total : ln.key === 'a' ? at.a : at.b)} r={3.5} fill={ln.color} />)}
          <text x={(W + PL) / 2} y={H - 6} textAnchor="middle" className="sens-axis">{def.label} — {fmtX(val)}</text>
        </svg>
        <div className="sens-legend">
          {lines.map((ln) => <span key={ln.key}><i style={{ background: ln.color }} />{ln.label}</span>)}
        </div>
      </div>

      <div className="sens-ctl">
        <input type="range" min={def.min} max={def.max} step={def.step} value={val} onChange={(e) => setVal(Number(e.target.value))} aria-label={def.label} />
        <div className="sens-read">
          <div className="sens-x">{def.label}: <b>{fmtX(val)}</b></div>
          {at && (
            <div className="sens-vals">
              <span><i style={{ background: '#8430ce' }} />Total support <b>{dollars(at.total)}</b>{at.payor !== null ? ` · ${L[at.payor]} pays` : ''}</span>
              <span><i style={{ background: 'var(--pa)' }} />{L[0]} net <b>{dollars(at.a)}</b></span>
              <span><i style={{ background: 'var(--pb)' }} />{L[1]} net <b>{dollars(at.b)}</b></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
